import React, { MouseEventHandler } from 'react';

import { Tooltip, Typography } from '@mui/material';
import clsx from 'clsx';
import { NavLink } from 'react-router-dom';

import { ReactComponent as Pointer } from 'assets/icons/general/pointer.svg';
import { useUserLogContext } from 'context/UserLogContext/UserLogContext';
import useLayoutStore from 'storages/layout';

import useStyles from './MenuLink.styles';

export type DisabledReason = 'under_construction' | 'premium' | 'unavailable_for_subject';

export type MenuLinkIcon = React.FunctionComponent<{ className?: string }> | string;

export type MenuLinkType = {
  label: string;
  to?: string;
  icon: MenuLinkIcon;
  disabled?: DisabledReason;
  iconWidth?: number;
  showIndicator?: boolean;
  onClick?: () => void;
};

type Props = MenuLinkType;

const resolveTooltip = (disabledReason?: DisabledReason): { title: string; tooltipClassName?: string; arrowClassName?: string } => {
  switch (disabledReason) {
    case 'premium':
      return { title: '⭐ Premium', tooltipClassName: 'MenuTooltip_premium', arrowClassName: 'MenuTooltipArrow_premium' };
    case 'under_construction':
      return {
        title: 'Pracujemy nad tym',
        tooltipClassName: 'MenuTooltip_underConstruction',
        arrowClassName: 'MenuTooltipArrow_underConstruction',
      };
    case 'unavailable_for_subject':
      return { title: 'Niedostepne dla tego przedmiotu' };
    default:
      return { title: '' };
  }
};

const renderDisabledBadge = (
  disabled: DisabledReason | undefined,
  title: string,
  classes: ReturnType<typeof useStyles>,
) => {
  if (!disabled || !title) {
    return null;
  }

  if (disabled === 'unavailable_for_subject') {
    return (
      <Typography className={clsx(classes.additionalTextBadge, classes.additionalTextBadgeMultiline)} component='span'>
        Niedostepne dla
        <br />
        tego przedmiotu
      </Typography>
    );
  }

  return (
    <Typography className={classes.additionalTextBadge} component='span'>
      {title}
    </Typography>
  );
};

const MenuLink: React.FC<Props> = ({ to, label, icon, disabled, iconWidth, showIndicator, onClick }) => {
  const { logEvent } = useUserLogContext();
  const { closeMenu } = useLayoutStore();
  const onLinkClick: MouseEventHandler<HTMLAnchorElement> = () => {
    logEvent('hamburger-click', { button: label });
    closeMenu();
  };
  const onActionClick = () => {
    logEvent('hamburger-click', { button: label });
    onClick?.();
    closeMenu();
  };

  const tooltip = resolveTooltip(disabled);
  const { classes } = useStyles({ iconWidth });
  const iconElement =
    typeof icon === 'string' ? (
      <img alt='' className={classes.iconImage} src={icon} />
    ) : (
      React.createElement(icon, { className: classes.icon })
    );
  const row = (
    <>
      {iconElement}
      <div className={classes.labelWrapper}>
        <Typography>{label}</Typography>
        {renderDisabledBadge(disabled, tooltip.title, classes)}
      </div>
    </>
  );

  if (disabled === 'under_construction') {
    return null;
  }

  return (
    // <Tooltip placement='left' title={tooltip.title} arrow classes={{ tooltip: tooltip.tooltipClassName, arrow: tooltip.arrowClassName }}>
    <div className={classes.wrapper}>
      {disabled ? (
        <div aria-disabled className={clsx(classes.root, classes.disabled)} role='presentation'>
          {row}
        </div>
      ) : onClick ? (
        <button type='button' className={classes.root} onClick={onActionClick}>
          {row}
        </button>
      ) : (
        <NavLink end className={classes.root} onClick={onLinkClick} to={to!}>
          {row}
        </NavLink>
      )}
      {showIndicator && <Pointer className={classes.pointer} />}
    </div>
    // </Tooltip>
  );
};

export default MenuLink;
